import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import {
  Paginated,
  PaginationQuery,
  RawPaginationMeta,
  mapPaginationMeta,
} from '../../../../core/models/pagination.model';

export interface DiningArea {
  id: string;
  name: string;
  slug: string;
  description?: string;
  sort_order: number;
  is_active: boolean;
  tables_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface DiningAreaRequest {
  name: string;
  description?: string;
  sort_order?: number;
  is_active?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

@Injectable({ providedIn: 'root' })
export class DiningAreasService {
  private readonly apiUrl = `${environment.apiUrl}/dining-areas`;

  constructor(private http: HttpClient) {}

  /** Full (unpaginated) list — used by dropdown consumers (e.g. table form). */
  getAll(): Observable<ApiResponse<DiningArea[]>> {
    return this.http.get<ApiResponse<DiningArea[]>>(this.apiUrl, {
      params: new HttpParams().set('all', 'true'),
    });
  }

  /** Paginated list for the dining areas list page. */
  getPage(query: PaginationQuery = {}): Observable<Paginated<DiningArea>> {
    const params = new HttpParams()
      .set('page', String(query.page ?? 1))
      .set('perPage', String(query.perPage ?? 25));

    return this.http
      .get<ApiResponse<DiningArea[]> & { meta?: RawPaginationMeta }>(this.apiUrl, {
        params,
      })
      .pipe(
        map((res) => ({
          items: res.data ?? [],
          meta: mapPaginationMeta(res.meta, res.data?.length ?? 0),
        })),
      );
  }

  get(id: string): Observable<ApiResponse<DiningArea>> {
    return this.http.get<ApiResponse<DiningArea>>(`${this.apiUrl}/${id}`);
  }

  create(data: DiningAreaRequest): Observable<ApiResponse<DiningArea>> {
    return this.http.post<ApiResponse<DiningArea>>(this.apiUrl, data);
  }

  update(id: string, data: Partial<DiningAreaRequest>): Observable<ApiResponse<DiningArea>> {
    return this.http.put<ApiResponse<DiningArea>>(`${this.apiUrl}/${id}`, data);
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  toggleActive(id: string): Observable<ApiResponse<{ id: string; is_active: boolean }>> {
    return this.http.patch<ApiResponse<{ id: string; is_active: boolean }>>(
      `${this.apiUrl}/${id}/toggle-active`,
      {},
    );
  }
}
